import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

export default function LegendBar({
  colors = ["#FBE9E7", "#FAD4C0", "#F8B9A3", "#F18C7F", "#D9534F", "#A63D3D"],
  labels,
  showLevels = false,
  borderRadius = 12,

  // NEW
  orientation = "horizontal", // "horizontal" | "vertical"
  dotSize = 10, // used in vertical mode
  rowGap = 0.5, // spacing between items in vertical mode
  maxWidth = 600, // container width cap for horizontal
  maxHeight, // vertical list max height (enables scroll)
}) {
  const { t } = useTranslation();
  const resolvedLabels = useMemo(() => {
    if (labels && labels.length) return labels;
    return [
      t("legend.veryLow"),
      t("legend.low"),
      t("legend.moderate"),
      t("legend.high"),
      t("legend.veryHigh"),
      t("legend.extremelyHigh"),
    ];
  }, [labels, t]);

  if (orientation === "vertical") {
    return (
      <Box
        role="list"
        aria-label={t("legend.title")}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: rowGap,
          maxHeight,
          overflowY: maxHeight ? "auto" : "visible",
          pr: maxHeight ? 1 : 0,
        }}
      >
        {resolvedLabels.map((label, i) => (
          <Box
            key={i}
            role="listitem"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              minHeight: Math.max(16, dotSize),
              wordBreak: "break-word",
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: dotSize,
                height: dotSize,
                borderRadius: "50%",
                backgroundColor: colors[i % colors.length],
                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.15)",
                flex: "0 0 auto",
              }}
            />
            <Typography sx={{ fontSize: 12, lineHeight: 1.25 }}>
              {showLevels ? `${i + 1}. ` : ""}
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  // HORIZONTAL (your current style, scroll-friendly)
  return (
    <Box sx={{ borderRadius: 2, maxWidth, overflowX: "auto" }}>
      <Box sx={{ display: "flex", borderRadius }}>
        {colors.map((color, index) => (
          <Box
            key={index}
            sx={{
              minWidth: 38,
              flex: 1,
              backgroundColor: color,
              height: 16,
              borderTopLeftRadius: index === 0 ? borderRadius : 0,
              borderBottomLeftRadius: index === 0 ? borderRadius : 0,
              borderTopRightRadius:
                index === colors.length - 1 ? borderRadius : 0,
              borderBottomRightRadius:
                index === colors.length - 1 ? borderRadius : 0,
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        {resolvedLabels.map((label, index) => (
          <Box key={index} sx={{ textAlign: "center", minWidth: 56, flex: 1 }}>
            <Typography sx={{ fontSize: 12 }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
